import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { IconButton, IconLoader } from 'components/common/Icon';
import { TextField } from 'components/common/TextField';
import formatPrice from 'utils/format-price';
import { Product } from 'utils/Product';
import ReactLoading from 'react-loading';
import { parseSearchTerms, searchWithinProducts } from 'utils/search';
import SearchField from 'components/common/SearchField';
import { Button } from 'components/common/Button';
import Rating from 'components/common/Rating';
import FilterDropdown from 'components/common/FilterDropdown';
import { editIcon, eyeOffIcon, eyeIcon, trashIcon, checkCircleIcon, alertCircleIcon } from 'assets/icons';
import { useProductOperations, useProducts } from 'app-state/product-cache';
import productFallback from 'assets/images/productFallback.png';

const productFields: (keyof Product)[] = [
  // 'id',
  'title',
  'quantity',
  'category',
  'price',
  'stars',
  'visible',
];

const ColumnHeader = ({ name }: { name: string }) => {
  return <th
    scope="col"
    className='px-2 py-1 md:px-6 md:py-3 text-xs md:text-s text-center text-gray-500 uppercase tracking-wider'
  >
    {name}
  </th>;
};

const ProductFieldCell = ({ product, field }: { product: Product, field: keyof Product }) => {
  if (field === 'visible') {
    return <input type='checkbox' disabled checked={product[field]} />;
  } else if (field === 'price') {
    return <>{formatPrice(product[field])}</>;
  } else if (field === 'title') {
    return <div className='flex flex-row justify-left'>
      <span className='hidden md:block md:flex-shrink-0' >
        <img
          src={product.image}
          alt={product.title}
          title={product.title}
          onError={e => (e.target as HTMLImageElement).src = productFallback}
          className='h-12 w-12 rounded-md border border-gray-400' />
      </span>
      <div className='flex ml-4 inline-block m-auto align-middle'>{product.title}</div>
    </div>;
  } else if (field === 'stars') {
    return <Rating readonly className='align-middle' size={13} ratingValue={product.stars} />;
  }
  return <>{product[field]}</>;
};

type ProductEditorRowProps = { product: Product, onChangeProductVisibility: (visibility: boolean) => Promise<unknown> }

const ProductEditorRow = ({ product, onChangeProductVisibility }: ProductEditorRowProps) => {
  const history = useHistory();
  const [deleteProductModalOpen, setDeleteProductModalOpen] = React.useState(false);
  const [confirmDeleteTitle, setConfirmDeleteTitle] = React.useState('');
  const [waitingOnVisibilityChange, setWaitingOnVisibilityChange] = React.useState(false);
  const { deleteProduct } = useProductOperations();

  const onConfirmDeleteProduct = () => {
    if (confirmDeleteTitle !== product.title) return;
    deleteProduct(product.id);
    setDeleteProductModalOpen(false);
  };

  const onSetVisibility = (visibility: boolean) => { // TODO rename
    setWaitingOnVisibilityChange(true);
    onChangeProductVisibility(visibility).then(() => setWaitingOnVisibilityChange(false));
  };

  return <React.Fragment>
    <tr>
      {productFields.map(field => <td key={`${product.id}-${field}`}>
        <ProductFieldCell
          product={product}
          field={field as keyof Product} /></td>)}
      <td>
        <div className='flex justify-center my-auto'>
          <IconButton
            data-locator='edit-product'
            label={`Edit ${product.title}`}
            onClick={() => history.push(`/admin/product-editor/${product.id}`)}
            src={editIcon}
            alt='edit' />
          {waitingOnVisibilityChange && <IconLoader />}
          {!waitingOnVisibilityChange && product.visible && <IconButton
            data-locator='hide-product'
            label={`Hide ${product.title}`}
            onClick={() => onSetVisibility(false)}
            src={eyeOffIcon}
            alt='hide' />}
          {!waitingOnVisibilityChange && !product.visible && <IconButton
            data-locator='make-product-visible'
            label={`Make ${product.title} visible`}
            onClick={() => onSetVisibility(true)}
            src={eyeIcon}
            alt='make visible' />}
          <IconButton
            data-locator='delete-product'
            label={`Delete ${product.title}`}
            onClick={() => setDeleteProductModalOpen(true)}
            src={trashIcon}
            alt='delete' />
        </div>
      </td>
    </tr>

    <ConfirmationModal
      id='confirm-delete-modal'
      isOpen={deleteProductModalOpen}
      heading={`Permanently Delete "${product.title}"`}
      confirmButtonText='Yes, delete'
      confirmButtonStyle={confirmDeleteTitle === product.title ? 'danger' : 'grey'}
      cancelButtonStyle='primary'
      onConfirm={onConfirmDeleteProduct}
      onCancel={() => setDeleteProductModalOpen(false)}
    >
      <p>
        <>Deleting this item will remove it permanently from the database. </>
        <>Are you sure you don&apos;t want to hide it instead?</>
      </p>

      <div className='flex justify-center mt-8 space-x-2'>
        <label htmlFor='confirm-delete-product-title' className='my-auto text-lg'>
          Enter the product title to delete:
        </label>
        <TextField
          id='confirm-delete-product-title'
          value={confirmDeleteTitle}
          onChange={event => setConfirmDeleteTitle(event.target.value)}
          validateInput={value => value === product.title} />
        <img src={confirmDeleteTitle === product.title
          ? checkCircleIcon
          : alertCircleIcon} />
      </div>
    </ConfirmationModal>
  </React.Fragment>;
};

type VisibilityFilterState = null | 'all' | 'visible' | 'invisible';
type StockStatusFilterState = null | 'all' | 'in-stock' | 'out-of-stock';
interface ProductAdminOptionsProps {
  currentVisibilityFilter: VisibilityFilterState,
  onVisibilityFilterChange: (filterState: VisibilityFilterState) => void,
  currentStockFilter: StockStatusFilterState,
  onStockFilterChange: (filterState: StockStatusFilterState) => void,
  productSearchFilter: string | null,
  setProductSearchFilter: (searchTerm: string) => void,
}

const ProductAdminOptions = ({
  currentVisibilityFilter,
  onVisibilityFilterChange,
  currentStockFilter,
  onStockFilterChange,
  productSearchFilter,
  setProductSearchFilter,
}: ProductAdminOptionsProps) => {
  const visibilityOptions: { value: VisibilityFilterState, label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'visible', label: 'Visible' },
    { value: 'invisible', label: 'Invisible' }];
  const inStockOptions: { value: StockStatusFilterState, label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'in-stock', label: 'In Stock' },
    { value: 'out-of-stock', label: 'Out of Stock' }];

  return <span className='flex flex-row font-medium'>
    <FilterDropdown
      name='Visibility'
      options={visibilityOptions}
      currentState={currentVisibilityFilter}
      onChange={(filterState) => onVisibilityFilterChange(filterState)} />
    <FilterDropdown
      name='Stock'
      options={inStockOptions}
      currentState={currentStockFilter}
      onChange={(filterState) => onStockFilterChange(filterState)} />
    <div className='grid place-content-end'>
      <SearchField
        label='Search administration table for products'
        className='pl-1 pb-2'
        searchTerm={productSearchFilter}
        setSearchTerm={setProductSearchFilter}
        onSubmit={() => null} />
    </div>
    <div className='mt-auto mb-2 ml-auto'>
      <Button
        className='font-bold'
        id='create-product-button'
        primary
        linkTo='/admin/product-creator'
      >Create Product</Button>
    </div>
  </span>;
};

const ProductAdmin = () => {
  const { products } = useProducts();
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilterState>(null);
  const [inStockFilter, setInStockFilter] = useState<StockStatusFilterState>(null);
  const [productSearchFilter, setProductSearchFilter] = useState<string | null>(null);
  const { setProductVisibility } = useProductOperations();

  const getFilteredProducts = () => {
    if (!products) return products;
    let filteredProducts = products;
    filteredProducts = (!visibilityFilter || visibilityFilter === 'all')
      ? filteredProducts
      : filteredProducts?.filter(
        p => (p.visible && visibilityFilter === 'visible') || (!p.visible && visibilityFilter === 'invisible'),
      );
    filteredProducts = (!inStockFilter || inStockFilter === 'all')
      ? filteredProducts
      : filteredProducts?.filter(
        p => (p.quantity <= 0 && inStockFilter === 'out-of-stock') || (p.quantity > 0 && inStockFilter === 'in-stock'),
      );
    filteredProducts = productSearchFilter
      ? searchWithinProducts(filteredProducts, parseSearchTerms(productSearchFilter).searchTerms)
      : filteredProducts;
    return filteredProducts;
  };

  return <React.Fragment>
    {!products && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
    {products && <>
      <ProductAdminOptions
        currentVisibilityFilter={visibilityFilter}
        onVisibilityFilterChange={setVisibilityFilter}
        currentStockFilter={inStockFilter}
        onStockFilterChange={setInStockFilter}
        productSearchFilter={productSearchFilter}
        setProductSearchFilter={setProductSearchFilter} />
      <table className='divide-y divide-gray-200 w-full mt-4 mb-8'>
        <thead className='bg-gray-50'>
          <tr>
            {productFields.map(field => <ColumnHeader key={`header-${field}`} name={field} />)}
            <ColumnHeader name='Actions' />
          </tr>
        </thead>
        <tbody>
          {getFilteredProducts()?.map(p => <ProductEditorRow
            key={p.id}
            product={p}
            onChangeProductVisibility={visibility => setProductVisibility(p, visibility)} />)}
        </tbody>
      </table>
    </>}
  </React.Fragment>;
};

export default ProductAdmin;
